import React, { memo, useCallback, useState, useMemo, useEffect } from 'react';

import {
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { ExpandMore, ChevronRight, Add, Remove } from '@material-ui/icons';
import { TreeView, TreeItem } from '@material-ui/lab';
import { isEqual, isEmpty, isNumber } from 'lodash';
import PropTypes from 'prop-types';

import { canURL, t } from '~/helpers';

function Tree({ items, onSelecteds, edition, loading }) {
  const [expanded, setExpanded] = useState([]);
  const [checked, setChecked] = useState([]);
  const [hidden, setHidden] = useState(false);

  const CRUD = useMemo(() => isEqual(canURL(), { read: true }), []);

  // Responsible for controlling the expansion of each node in the tree
  const handleExpandInTheTree = useCallback((node) => {
    const id = node.toString();

    setExpanded((state) =>
      state.includes(id) ? state.filter((s) => s !== id) : state.concat(id)
    );
  }, []);

  // Returns array of all tasks and tree nodes
  const handleRetrieveIdOfChildrenAndTasks = useCallback(
    ({ id, nodes, tasks }) => {
      const data = [id];

      const nodeTasks = ({ code, task }) => {
        task.forEach((node) => data.push(`${code}.${node.codigotarefa}`));
      };

      const nodeTree = (node) => {
        node.forEach(({ idmenu, tarefas, itens }) => {
          data.push(idmenu.toString());

          if (!isEmpty(tarefas)) nodeTasks({ code: idmenu, task: tarefas });
          if (!isEmpty(itens)) nodeTree(itens);
        });
      };

      if (!isEmpty(tasks)) nodeTasks({ code: id, task: tasks });
      if (!isEmpty(nodes)) nodeTree(nodes);

      return [...new Set(data)];
    },
    []
  );

  // Select and return data from previous node
  const handleSelectDataFromPrevious = useCallback(
    (id) => {
      const data = [];

      const node = (nodes) => {
        if (nodes.idmenu === id) data.push(nodes);
        if (isEmpty(data) && !isEmpty(nodes.itens)) {
          nodes.itens.forEach((i) => node(i));
        }
      };

      items.forEach((i) => node(i));

      return data.shift();
    },
    [items]
  );

  // Responsible for fetching and returning the id of the above nodes
  const handleIdOfTheNodeAbove = useCallback(
    ({ id }) => {
      if (id.toString() !== '0') {
        const keys = [id.toString()];

        const node = (nodes) => {
          const key = [...keys].pop();

          if (nodes.idmenu.toString() === key.toString()) {
            if (nodes.idpaimenu.toString() !== '0') {
              keys.push(nodes.idpaimenu.toString());
              items.forEach((i) => node(i));
            }
          } else if (!isEmpty(nodes.itens)) nodes.itens.forEach((i) => node(i));
        };

        items.forEach((i) => node(i));

        return keys;
      }

      return [];
    },
    [items]
  );

  // Returns array with levels above the selected that passed verification
  const handleLevelAboveSelected = useCallback(
    ({ data, checkeds }) => {
      const keys = [data.idmenu.toString()];
      let previous = data.idpaimenu;

      const nodes = (node) => {
        if (previous !== 0) {
          if (node.idmenu === previous) {
            const index = node.itens
              .map((i) => i.idmenu.toString())
              .some((i) => checkeds.includes(i));

            if (index) {
              keys.push(node.idmenu.toString());
              previous = node.idpaimenu;
              items.forEach((i) => nodes(i));
            }
          } else node.itens.forEach((i) => nodes(i));
        }
      };

      items.forEach((i) => nodes(i));

      return keys;
    },
    [items]
  );

  // Select item and store from array
  const handleSelectInTheTree = useCallback(
    ({ id, node, props = [] }) => {
      const index = checked.concat(props).indexOf(id.toString());
      const arrNode = handleIdOfTheNodeAbove({ id: node.idpaimenu });

      if (isNumber(id)) {
        const data = handleRetrieveIdOfChildrenAndTasks({
          id: id.toString(),
          nodes: node.itens,
          tasks: node.tarefas,
        });

        // Previous node treatment
        if (node.idpaimenu) {
          const nodes = handleSelectDataFromPrevious(node.idpaimenu);

          if (nodes) {
            const indexs = nodes.tarefas
              .map((i) => `${nodes.idmenu}.${i.codigotarefa}`)
              .concat(nodes.idmenu.toString())
              .concat(arrNode);

            const arrayNode = handleRetrieveIdOfChildrenAndTasks({
              id: nodes.idmenu.toString(),
              nodes: nodes.itens,
              tasks: nodes.tarefas,
            })
              .filter((n) => !indexs.concat(data).includes(n))
              .every((n) => checked.concat(props).includes(n));

            // Add elements from parent item to array
            if (arrayNode) indexs.forEach((n) => data.push(n));
          }
        }

        if (index === -1) {
          setChecked([...new Set(checked.concat(props).concat(data))]);
        } else {
          setChecked(checked.concat(props).filter((s) => !data.includes(s)));
        }
      }

      if (!isNumber(id)) {
        const data = [node.idmenu.toString(), id];

        if (index === -1) {
          const tree = [];
          const tasks = node.tarefas
            .map((task) => `${node.idmenu}.${task.codigotarefa}`)
            .filter((task) => checked.concat(props).includes(task))
            .concat(id).length;

          if (node.tarefas.length === tasks) {
            handleLevelAboveSelected({
              data: node,
              checkeds: checked.concat(props),
            }).forEach((i) => tree.push(i));
          }

          setChecked((state) =>
            node.tarefas.length === tasks
              ? [...new Set(state.concat(data).concat(tree))]
              : state.concat(id)
          );
        } else {
          const arrayNode = arrNode.concat(data);
          setChecked((state) => state.filter((s) => !arrayNode.includes(s)));
        }
      }
    },
    [checked]
  );

  // Responsible for checking if any children of the node were selected
  const handleChecksIfItHasSelectedChildren = useCallback(
    (node) => {
      let indeterminate = false;

      const data = handleRetrieveIdOfChildrenAndTasks({
        id: node.idmenu.toString(),
        nodes: node.itens,
        tasks: node.tarefas,
      });

      data.forEach((item) => {
        if (checked.includes(item)) indeterminate = true;
      });

      return indeterminate;
    },
    [checked]
  );

  // It loops through the entire tree and assembles all the html
  const handleWalkThroughTree = useCallback(
    (node) => {
      // tree node tasks
      const tasks = node.tarefas
        .filter(() => isEmpty(node.itens))
        .map((task) => ({
          ...task,
          node: `${node.idmenu}.${task.codigotarefa}`,
          check: checked.includes(`${node.idmenu}.${task.codigotarefa}`),
        }));

      // children of the tree number
      const childrens = isEmpty(node.itens) ? [] : node.itens;

      const nodeChecked = checked.includes(node.idmenu.toString());
      const nodeIndeterminate = nodeChecked
        ? false
        : handleChecksIfItHasSelectedChildren(node);

      return (
        <TreeItem
          key={Math.random().toString()}
          nodeId={node.idmenu.toString()}
          onIconClick={() => handleExpandInTheTree(node.idmenu)}
          onLabelClick={() =>
            handleSelectInTheTree({
              id: node.idmenu,
              node,
            })
          }
          label={
            <FormControlLabel
              label={t(node.codigolabelsistema)}
              control={
                <Checkbox
                  disabled={CRUD}
                  checked={nodeChecked}
                  indeterminate={nodeIndeterminate}
                />
              }
            />
          }
        >
          {tasks.map((task) => (
            <TreeItem
              key={Math.random().toString()}
              nodeId={task.node}
              onLabelClick={() =>
                handleSelectInTheTree({
                  id: task.node,
                  node,
                })
              }
              label={
                <FormControlLabel
                  label={t(task.codigolabelsistema)}
                  control={<Checkbox disabled={CRUD} checked={task.check} />}
                />
              }
            />
          ))}

          {childrens.map((children) => handleWalkThroughTree(children))}
        </TreeItem>
      );
    },
    [CRUD, checked]
  );

  // Show or hidden all node items
  const handleToggleTree = useCallback(
    (showOrHidden) => {
      const data = [];

      const nodes = (node) => {
        data.push(node.idmenu.toString());
        if (node.itens) node.itens.forEach((i) => nodes(i));
      };

      items.forEach((i) => nodes(i));

      setExpanded(showOrHidden ? [] : [...new Set(data)]);
      setHidden((state) => !state);
    },
    [items]
  );

  useEffect(() => {
    if (edition) {
      const data = [];
      const hasChildrens = [];

      // loops through the entire loop and logs all selected tasks
      const nodes = (node) => {
        const tasks = node.tarefas
          .filter((task) => task.selecionadatarefa === 1)
          .map((task) => `${node.idmenu}.${task.codigotarefa}`);

        // Checks if it has no children and records the entire data array
        if (isEmpty(node.itens)) {
          tasks.forEach((task) => {
            handleSelectInTheTree({ id: task, node, props: data });
            data.push(task);
          });

          if (tasks.length === node.tarefas) data.push(node.idmenu.toString());
        } else {
          hasChildrens.push(node.idmenu);
          node.itens.forEach((i) => nodes(i));
        }
      };

      // loops through all items that have children and confirms they are selected
      const nodesHasChildrens = (id) => {
        const loopNodes = (node) => {
          if (node.idmenu === id) {
            const index = [];
            const tasks = [id.toString()];

            node.itens.forEach((item) => {
              item.tarefas.forEach((i) => {
                index.push(`${item.idmenu}.${i.codigotarefa}`);
              });
            });

            node.tarefas.forEach((task) => {
              tasks.push(`${node.idmenu}.${task.codigotarefa}`);
            });

            if (index.every((i) => data.includes(i))) {
              tasks.forEach((task) => data.push(task));
              setChecked((state) => state.concat(tasks));
            }
          } else node.itens.forEach((i) => loopNodes(i));
        };

        items.forEach((i) => loopNodes(i));
      };

      items.forEach((i) => nodes(i));

      hasChildrens.reverse().forEach((i) => nodesHasChildrens(i));
    }
  }, [edition]);

  useEffect(() => {
    if (!isEmpty(checked)) {
      const nodes = (node) => {
        const { tarefas, idmenu, itens } = node;
        let childrens = [];

        // logs all tasks
        const tasks = tarefas.map((task) => ({
          ...task,
          selecionadatarefa: Number(
            checked.includes(`${idmenu}.${task.codigotarefa}`)
          ),
        }));

        if (!isEmpty(node.itens)) {
          childrens = itens.map((item) => nodes(item));
        }

        return { ...node, tarefas: tasks, itens: childrens };
      };

      onSelecteds(items.map((item) => nodes(item)));
    }
  }, [checked]);

  return (
    <>
      <IconButton
        type="button"
        disabled={loading}
        onClick={() => handleToggleTree(hidden)}
      >
        {loading ? (
          <CircularProgress size={25} />
        ) : hidden ? (
          <Remove />
        ) : (
          <Add />
        )}
      </IconButton>

      <TreeView
        defaultCollapseIcon={<ExpandMore />}
        defaultExpandIcon={<ChevronRight />}
        expanded={expanded}
      >
        {items.map((node) => handleWalkThroughTree(node))}
      </TreeView>
    </>
  );
}

Tree.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  onSelecteds: PropTypes.func.isRequired,
  edition: PropTypes.bool,
  loading: PropTypes.bool,
};

Tree.defaultProps = {
  edition: false,
  loading: false,
};

export default memo(Tree);
